import { Form } from '@remix-run/react';
import { createContext, FormEvent, useContext, useEffect, useState } from 'react';
import {
  extend,
  type ExtensionFactory,
  type ImmutableArray,
  type ImmutableObject,
  none,
  type State,
  useHookstate,
} from '@hookstate/core';
import { type Subscribable, subscribable } from '@hookstate/subscribable';
import { v4 } from 'uuid';
import { BaseSchema } from 'valibot';
import * as v from 'valibot';

type ValidatedFormState = { [Key: string]: string | string[] | ValidatedFormState | ValidatedFormState[] };

type ExtensionData = {
  identifier: string;
};

type ValidatedFormContext = {
  state: State<ValidatedFormState, Subscribable>;
  touched: State<boolean>;
  prefix?: string[];
  schema: BaseSchema<any, any, any>;
};

export function extension<S, E>(): ExtensionFactory<S, E, ExtensionData> {
  return () => {
    const ids = new Map<string, string>();

    return {
      onCreate(rootState, _extensionMethods) {
        return {
          identifier(state) {
            let id = ids.get(state.path.join(''));

            if (!id) {
              id = v4();

              ids.set(state.path.join(''), id);
            }

            return id;
          },
        };
      },
    };
  };
}

const context = createContext<ValidatedFormContext | null>(null);

function getNestedState(state: State<ValidatedFormState, Subscribable>, name: string) {
  const path = name.split(/[.[\]]/).filter((p) => p !== '');

  let next = path.shift();

  if (!next) {
    throw new Error('Invalid name');
  }

  let nested = state.nested(next);

  while (path.length > 0) {
    next = path.shift();

    if (next) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      nested = nested.nested(next);
    }
  }

  return nested;
}

function useValibotFormState(ctx: ValidatedFormContext, name: string) {
  let prefixed = '';

  if (ctx.prefix) {
    for (let i = 0; i < ctx.prefix.length; i++) {
      if (i === 0) {
        prefixed = ctx.prefix[i];
      } else {
        prefixed = `${prefixed}[${ctx.prefix[i]}]`;
      }
    }
  }

  if (prefixed) {
    prefixed = `${prefixed}[${name}]`;
  } else {
    prefixed = name;
  }

  const state = getNestedState(useHookstate(ctx.state), prefixed);

  return { prefixed, state };
}

export function useIsValibotFormValid(): true | string {
  const ctx = require_context();

  const scoped = useHookstate(ctx.state).get({ noproxy: true });

  const result = v.safeParse(ctx.schema, scoped);

  if (result.success) {
    return true;
  }

  return result.issues[0].message;
}

export function useIsValibotFormTouched() {
  const ctx = require_context();

  return useHookstate(ctx.touched).get();
}

function useFieldTouched(state: State<any, Subscribable>) {
  const [touched, setTouched] = useState(false);

  useEffect(
    state.subscribe(() => {
      setTouched(true);
    }),
    [state],
  );

  return touched;
}

export function useValibotData<T>(schema?: BaseSchema<any, T, any>): T {
  const ctx = require_context();

  return v.parse(schema ?? ctx.schema, ctx.state.get({ noproxy: true }));
}

export function useValibotFieldObject<T extends object>(
  name: string,
): [string, T | undefined, (value: T | undefined | null) => void] {
  const ctx = require_context();

  const { state, prefixed } = useValibotFormState(ctx, name);

  const value = state.get();

  return [
    prefixed,
    // eslint-disable-next-line @typescript-eslint/no-base-to-string
    value === '' || value === null || value === undefined ? undefined : (value as T),
    (value: T | undefined | null) => {
      if (value === undefined || value === null) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        state.set(none);
      } else {
        state.merge(value);
      }
    },
  ];
}

export function useValibotFieldSingle<T extends string>(
  name: string,
): [string, T | undefined, (value: string | undefined | null) => void, string | null, boolean] {
  const ctx = require_context();

  const { state, prefixed } = useValibotFormState(ctx, name);

  const scoped = useHookstate(ctx.state).get({ noproxy: true, stealth: true });

  const result = v.safeParse(ctx.schema, scoped);

  let error: string | null = null;

  if (!result.success) {
    error = 'Not Implemented'; //result.error.errors.filter((e) => e.path[0] === prefixed).shift()?.message ?? null;
  }

  const value = state.get();

  return [
    prefixed,
    // eslint-disable-next-line @typescript-eslint/no-base-to-string
    value === '' || value === null || value === undefined ? undefined : (value.toString() as T),
    (value: string | undefined | null) => {
      if (value === undefined || value === '' || value === null) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        state.set(none);
      } else {
        state.set(value);
      }
    },
    error,
    useFieldTouched(state),
  ];
}

export function useValibotFieldTouched(name: string) {
  const ctx = require_context();

  const { state } = useValibotFormState(ctx, name);

  return useFieldTouched(state);
}

export function useValibotFieldError(name: string) {
  const ctx = require_context();

  const scoped = useHookstate(ctx.state).get({ noproxy: true });

  const result = v.safeParse(ctx.schema, scoped);

  if (result.success) {
    return null;
  }

  const path = ctx.prefix ? `${ctx.prefix.join('.')}.${name}` : name;

  for (const issue of result.issues) {
    if (Array.isArray(issue.path)) {
      const issuePath = issue.path
        .map((p: { key: string | number }) => (typeof p.key === 'number' ? `[${p.key}]` : p.key))
        .join('.')
        .replace(/\.\[/g, '[');

      if (path === issuePath && typeof issue.message === 'string') {
        return issue.message;
      }
    }
  }

  return null;
}

export function useValibotFieldSingleBoolean(name: string): boolean | undefined {
  const [_name, value] = useValibotFieldSingle(name);

  if (value === undefined || value === null) {
    return undefined;
  }

  return value === 'true';
}

export function useValibotFieldSingleValue<T extends string>(name: string): T | undefined {
  const [_name, value] = useValibotFieldSingle<T>(name);

  return value;
}

export function useValibotFieldMultiple(
  name: string,
): [string, string[], (value: string[]) => void, string | null, boolean] {
  const ctx = require_context();

  const { state, prefixed } = useValibotFormState(ctx, name);

  const scoped = useHookstate(ctx.state).get({ noproxy: true, stealth: true });

  const result = v.safeParse(ctx.schema, scoped);

  const error = result.issues?.find((i) => i.path?.[0].key === name)?.message;

  const value = state.get() as ImmutableArray<string>;

  return [
    prefixed,
    Array.from(value || []).map(String),
    (value: string[]) => {
      state.set(value);
    },
    error,
    useFieldTouched(state),
  ];
}

export function useValibotFieldArray(
  name: string,
): [Array<{ name: string; remove: () => void }>, (value?: Record<string, any>) => void, () => void] {
  const ctx = require_context();

  const { state, prefixed } = useValibotFormState(ctx, name) as {
    state: State<ValidatedFormState[], Subscribable>;
    prefixed: string;
  };

  const scoped = useHookstate(ctx.state);
  const arrayMissing = !('map' in state);

  useEffect(() => {
    if (!arrayMissing) {
      return;
    }

    const parent = prefixed.split('.');
    const property = parent.pop();

    if (!property) {
      throw new Error('Invalid name');
    }

    if (parent.length == 0) {
      scoped.merge({ [property]: [] });
    } else {
      getNestedState(scoped, parent.join('.')).merge({ [property]: [] });
    }
  }, []);

  if (arrayMissing) {
    return [
      [],
      () => {
        throw new Error('Cannot add to state array before state array exists');
      },
      () => {
        // nothing to clear
      },
    ];
  }

  return [
    state.map((nested, index) => ({
      name: `${prefixed}[${index}]`,
      remove() {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        nested.set(none);
      },
    })),
    (value) => {
      state.merge([value ? { ...value } : ({} as any)]);
    },
    () => {
      state.set([]);
    },
  ];
}

type RecursivePartial<T> = {
  [P in keyof T]?: RecursivePartial<T[P]>;
};

function require_context() {
  const ctx = useContext(context);

  if (!ctx) {
    throw new Error('<NestedFields /> must be used within a <ValidatedForm />');
  }

  return ctx;
}

export function ValibotNestedForm({ children, name }: { children: React.ReactNode; name: string }) {
  const ctx = require_context();

  return <context.Provider value={{ ...ctx, prefix: [...(ctx?.prefix ?? []), name] }}>{children}</context.Provider>;
}

export interface ValibotFormProps<T = object> {
  children: React.ReactNode;
  schema: BaseSchema<any, T, any>;
  defaultValues?: RecursivePartial<T>;
  onValid?: (data: T) => void;
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
  method?: 'GET' | 'POST';
  className?: string;
  action?: string;
}

export default function ValibotForm<T = object>(props: ValibotFormProps<T>) {
  const state = useHookstate((props.defaultValues ?? {}) as ValidatedFormState, extend(subscribable(), extension()));
  const touched = useHookstate(false);

  useEffect(() => {
    return state.subscribe(() => {
      touched.set(true);
    });
  }, [touched]);

  useEffect(() => {
    const onChange = (value: ImmutableObject<ValidatedFormState>) => {
      const { onValid } = props;

      if (!onValid) {
        return;
      }

      const result = v.safeParse(props.schema, value);

      if (result.success) {
        onValid(result.output);
      }
    };

    return state.subscribe(onChange);
  }, [state, props.onValid]);

  return (
    <context.Provider value={{ state, schema: props.schema, touched }}>
      <Form
        method={props.method ?? 'POST'}
        action={props.action}
        autoComplete="off"
        navigate={!props.action || props.method === 'GET'}
        className={props.className ?? 'w-full'}
        onSubmit={(e) => {
          touched.set(true);

          const data = state.get({ noproxy: true, stealth: true });

          const result = v.safeParse(props.schema, data);

          if (!result.success) {
            e.preventDefault();
          } else if (props.onSubmit) {
            props.onSubmit(e);
          }
        }}
      >
        {props.children}
      </Form>
    </context.Provider>
  );
}
